/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import { FormattedNumber } from "react-intl";
import { connect } from "react-redux";
import * as actions from "Actions";
import classnames from "classnames";
import CurrencyInput from "Utils/CurrencyInput";
import { toNumber } from "HelperFunctions/general";
import validations from "HelperFunctions/validations";
import Radio from "Utils/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import PropayBankForm from "Components/profile/bankAccounts/PropayBankForm";
import WorldpayBankForm from "Components/profile/bankAccounts/WorldpayBankForm";
import WorldpayHostedPaymentModal from "Components/profile/creditCards/WorldpayHostedPaymentModal";
import StripeSecureCardModal from "Components/profile/creditCards/StripeSecureCardModal";
import StandardSelectField from "Utils/redux_form_inputs/StandardSelectField";
import { collectStripeBankAccount } from "Utils/collectStripeBankAccount";
import ScrollArea from "Utils/react-scrollbar";
import CurrencyLabel from "Utils/CurrencyLabel";

class AutobookPayment extends React.Component {
  state = {
    paymentSelection: "credit_card",
    errors: {},
    billingStatus: "",
    transaction: {
      amount: "",
      payment_type: "credit_card",
      payment_date: new Date(),
      card_token: "",
      bank_token: "",
      check_number: ""
    },
    new_card: null,
    new_bank: null,
    amountType: "",
    customAmount: "",
    worldpayModalOpen: false,
    stripeModalOpen: false,
    cardNickname: ""
  };

  componentDidMount() {
    this.setState({
      amountType: "amountRemaining",
      customAmount: this.getPaymentAmount("amountRemaining")
    }, () => this.updateSurchargeInStore());
  }

  // TD1-10212: collect a us_bank_account PaymentMethod via the Stripe SDK (location-scoped,
  // no rental exists yet in the autobook/cart flow), replacing the legacy Plaid link.
  handleStripeBankClick = async () => {
    const { customer, location, openLoadingSpinner, closeLoadingSpinner, setErrors } = this.props;
    try {
      openLoadingSpinner("Connecting your bank...");
      const paymentMethodId = await collectStripeBankAccount({
        locationId: location.id,
        customerId: customer.id,
        billingDetails: {
          name: `${customer.firstName || ""} ${customer.lastName || ""}`.trim(),
          email: customer.email
        }
      });
      closeLoadingSpinner();
      this.setState({
        new_bank: { payment_method_id: paymentMethodId },
        transaction: { ...this.state.transaction, bank_token: "new_bank" }
      });
    } catch (error) {
      closeLoadingSpinner();
      setErrors({ body: error.message });
    }
  };

  // TD1-10397: clear the collected bank so the "Pay with Bank Account" button reappears.
  handleChangeStripeBank = () => {
    this.setState({ new_bank: null });
  };

  isSigned() {
    const event = this.props.cart.event;
    const signatureRequired = this.props.location.signatureRequired;
    if (!signatureRequired) {
      return true;
    }
    return typeof event.rentalSignaturesAttributes !== "undefined";
  }

  validate = () => {
    const errors = {};
    const amountDue = this.calculateAmountDue();
    const { paymentSelection, transaction, customAmount, new_bank } = this.state;
    const { location } = this.props;
    if (!this.isSigned()) {
      errors.body = "Please sign the order.";
    }
    if (
      paymentSelection === "credit_card" &&
      validations.required(transaction.card_token, true)
    ) {
      errors.body = "Please select a credit card.";
    }
    if (
      paymentSelection === "bank" &&
      validations.required(transaction.bank_token, true)
    ) {
      errors.body = "Please select a bank account.";
    }
    // TD1-10397: on Stripe, "New Bank Account" must have a collected PaymentMethod (pm_) before
    // reserving — otherwise the backend has no bank to charge and falls to the dead Plaid path.
    if (
      paymentSelection === "bank" &&
      transaction.bank_token === "new_bank" &&
      location.paymentProcessor === "stripe" &&
      !(new_bank && new_bank.payment_method_id)
    ) {
      errors.body = "Please add a bank account before continuing.";
    }
    if (customAmount > amountDue || customAmount < 0) {
      errors.body = "The Custom Amount you entered is invalid!";
    }
    return errors;
  };

  isOnlyLetters = (str) => {
    let strWithoutSpaces = str.replace(/\s/g, "");
    return /^[a-zA-Z]+$/.test(strWithoutSpaces);
  };

  isOnlyNumbers = (str) => {
    let strWithoutSpaces = str.replace(/\s/g, "");
    return /^\d+$/.test(strWithoutSpaces);
  };

  validateMerchantAccount = (location, paymentProcessor) => {
    const error = {};
    if (location.isOnTrial) {
      error.merchantAccount =
        "Location is on trial and cannot accept payments";
    }
    if (paymentProcessor === "stripe" && !location.hasStripeAuthentication) {
      error.merchantAccount = "Merchant account is not setup for Stripe";
    }
    if (paymentProcessor === "propay" && !location.hasPropayAuthentication) {
      error.merchantAccount = "Merchant account is not setup for Propay";
    }
    if (
      (paymentProcessor === "worldpay" ||
        paymentProcessor === "launchpay") &&
      !location.hasWorldpayAuthentication
    ) {
      error.merchantAccount = "Merchant account is not setup.";
    }
    return error;
  };

  validateTransactionAmountAndType = (transaction, amount) => {
    const error = {};
    const { payment_type } = transaction;
    const cardTypes = ["new_credit_card", "credit_card"];
    const bankTypes = ["new_bank", "bank"];

    if (amount <= 0) {
      error.noAmount = "Charge amount must be greater than 0.";
    }
    if (cardTypes.includes(payment_type) && amount < 0.5) {
      error.amount = "The minimum charge is $0.50";
    } else if (bankTypes.includes(payment_type) && amount < 1.5) {
      error.amount = "The minimum bank charge is $1.50.";
    }
    return error;
  };

  validateNewBank = (newBank) => {
    const errors = {};
    const { account_number, bank_number, first_name, last_name } = newBank;
    if (!account_number) {
      errors.accountNumber = "Account number cannot be blank.";
    }
    if (!this.isOnlyNumbers(account_number)) {
      errors.accountNumber = "Account number must contain only numbers.";
    }
    if (!bank_number) {
      errors.bankNumber = "Routing number cannot be blank.";
    }
    if (!this.isOnlyNumbers(bank_number)) {
      errors.notNumber = "Routing number must contain only numbers.";
    }
    if (bank_number.length !== 9) {
      errors.bankNumber = "Routing number must be 9 digits.";
    }
    if (!this.isOnlyLetters(first_name) || !this.isOnlyLetters(last_name)) {
      errors.accountName = "Account name must be letters only.";
    }
    return errors;
  };

  validatePayments = (transaction, location, paymentProcessor) => {
    const error = {};
    const { payment_type } = transaction;
    const { hasWorldpayAuthentication } = location;
    const types = ["credit_card", "new_credit_card"];
    if (
      types.includes(payment_type) &&
      !hasWorldpayAuthentication &&
      (paymentProcessor === "worldpay" || paymentProcessor === "launchpay")
    ) {
      error.paymentError = `This business isn't ready to accept ${paymentProcessor} payments.`;
    }
    if (payment_type === "new_bank" && !hasWorldpayAuthentication) {
      error.paymentError = "This business isn't ready to accept bank payments.";
    }
    return error;
  };

  validateCardType = (cardType) => {
    const error = {};
    if (cardType === "Select Card Type") {
      error.cardType = "A card type must be selected.";
    }
    return error;
  };

  validateCardSelection = (cardSelected) => {
    const error = {};
    if (cardSelected === "Select a Credit Card") {
      error.cardSelected = "Please select a card.";
    }
    return error;
  };

  validateBankSelection = (bankSelected) => {
    const error = {};
    if (bankSelected === "Select a Bank Account") {
      error.bankSelected = "Please select a bank account";
    }
    return error;
  };

  validateBankType = (paymentMethodType, checkType, companyName) => {
    const error = {};
    if (paymentMethodType === "Select Payment Method Type") {
      error.bankType = "You must select an account type.";
    }
    if (checkType === "Check Type") {
      error.checkType = "You must select a check type";
    }
    if (checkType === "Business" && !companyName) {
      error.companyName = "Please enter a Company name";
    }
    return error;
  };

  calculateAmountDue = () => {
    const { event } = this.props.cart;
    if (!event.billingLines) return;
    return event.billingLines.find((bl) => bl.kind === "orderTotal").finalValue;
  };

  calculateMinimumDepositFee = () => {
    const { event } = this.props.cart;
    if (!event.additionalInfo) return;
    return event.additionalInfo.minimum_payment_for_autobook;
  };

  calculateMinimumDeposit = () => {
    const amountDue = this.calculateAmountDue();
    const minimumDepositFee = this.calculateMinimumDepositFee();
    return Math.min(amountDue, minimumDepositFee);
  };

  buildRailsObject = () => {
    const { location, customer } = this.props;
    const { transaction, new_card, new_bank, paymentSelection, amountType } = this.state;
    const newTransaction = {
      ...transaction,
      customer_id: customer.id,
      amount: this.getPaymentAmount(amountType),
      payment_source: "cp",
      payment_type:
        paymentSelection === "credit_card"
          ? transaction.card_token === "new_credit_card"
            ? "new_credit_card"
            : "credit_card"
          : transaction.bank_token === "new_bank"
            ? "new_bank"
            : "bank"
    };
    return {
      location_id: location.id,
      transaction: newTransaction,
      billing_note: {
        author_name: customer.firstName + " " + customer.lastName,
        message: "From Storefront",
        note_type: "Payment"
      },
      new_card,
      new_bank,
      send_receipt: true
    };
  };

  toggleWorldpayModal = () => {
    this.setState((prev) => ({ worldpayModalOpen: !prev.worldpayModalOpen }));
  };

  handleWorldpayCardSaved = (savedCard) => {
    // Refresh customer data to get the new card
    const { location, fetchProfile } = this.props;
    if (fetchProfile) {
      fetchProfile(location.id);
    }

    // Set the new card as selected
    const cardToken = savedCard.creditCard?.token || savedCard.token;
    if (cardToken) {
      const newTransaction = {
        ...this.state.transaction,
        card_token: cardToken,
      };
      this.setState({ transaction: newTransaction }, () => this.updateSurchargeInStore());
    }

    this.toggleWorldpayModal();
  };

  toggleStripeModal = () => {
    this.setState((prev) => ({ stripeModalOpen: !prev.stripeModalOpen }));
  };

  handleStripeCardSaved = (savedCard) => {
    const { location, fetchProfile } = this.props;
    if (fetchProfile) {
      fetchProfile(location.id);
    }
    const card =
      savedCard?.credit_card ??
      savedCard?.creditCard ??
      savedCard?.data ??
      savedCard;
    const cardToken =
      card?.token ?? card?.id ?? savedCard?.token ?? savedCard?.id;
    if (cardToken) {
      const newTransaction = {
        ...this.state.transaction,
        card_token: cardToken,
      };
      this.setState({ transaction: newTransaction }, () => this.updateSurchargeInStore());
    }
    this.toggleStripeModal();
  };

  handleSubmitNewPropayBank = (values) => {
    this.setState({ new_bank: values }, this.handleSubmit);
  };

  handleSubmit = (e = null) => {
    if (e) e.preventDefault();
    const { customAmount, new_bank, paymentSelection, transaction } = this.state;
    const validateErrors = this.validate();
    const { location, setCartTransactionProps, handleReserveQuote } = this.props;
    const cardTypes = ["new_credit_card", "credit_card"];
    const bankTypes = ["new_bank", "bank"];
    let cardTypeError, newBankErrors, bankTypeErrors;
    let paymentMethodType = document.getElementsByName("payment_method_type")[0]?.value;
    let checkType, companyName;
    const paymentProcessor = location.paymentProcessor;
    if (cardTypes.includes(paymentSelection)) {
      cardTypeError = this.validateCardType(paymentMethodType);
    } else if (bankTypes.includes(paymentSelection)) {
      if (new_bank && paymentProcessor !== "stripe") {
        newBankErrors = this.validateNewBank(new_bank);
      }
      checkType = document.getElementsByName("check_type")[0]?.value;
      companyName = document.getElementsByName("company_name")[0]?.value;
      bankTypeErrors = this.validateBankType(paymentMethodType, checkType, companyName);
    }
    const merchantAccountErrors = this.validateMerchantAccount(location, paymentProcessor);
    const transactionAmountAndTypeErrors = this.validateTransactionAmountAndType(transaction, customAmount);
    const paymentValidationErrors = this.validatePayments(transaction, location, paymentProcessor);
    const errors = {
      ...this.validate(),
      ...merchantAccountErrors,
      ...transactionAmountAndTypeErrors,
      ...newBankErrors,
      ...cardTypeError,
      ...bankTypeErrors,
      ...paymentValidationErrors,
    };
    if (Object.keys(errors).length > 0) {
      this.setState({ errors: errors });
      this.props.setErrors(errors);
    } else {
      setCartTransactionProps(this.buildRailsObject());
      handleReserveQuote(e);
    }
  };

  updateSurchargeInStore = () => {
    const { location, creditCardPercent, setCartProps, customer } = this.props;
    const { rental } = this.props;
    const { paymentSelection, transaction, amountType } = this.state;
    const isCreditCard = paymentSelection === "credit_card";
    const hasFee = location.chargeCreditCardFee && (rental?.rentalSetting == null || rental?.rentalSetting?.applyCcFee) && creditCardPercent > 0;

    if (isCreditCard && hasFee) {
      const amount = Number(this.getPaymentAmount(amountType)) || 0;
      const isEstimate = !transaction.card_token || transaction.card_token === "new_credit_card";

      let isCreditBinType = true;
      if (transaction.card_token && transaction.card_token !== "new_credit_card") {
        const selectedCard = this.availableStorefrontCreditCards().find((cc) => cc.token === transaction.card_token);
        if (selectedCard) {
          isCreditBinType = selectedCard.binType === 'credit';
        }
      }

      const surchargeAmount = isCreditBinType ? (creditCardPercent / 100) * amount : 0;
      setCartProps({
        ccSurcharge: surchargeAmount,
        ccSurchargeIsEstimate: isEstimate,
        totalAmountBeingPaid: amount + surchargeAmount,
      });
    } else {
      const amount = Number(this.getPaymentAmount(amountType)) || 0;
      setCartProps({
        ccSurcharge: null,
        ccSurchargeIsEstimate: null,
        totalAmountBeingPaid: amount,
      });
    }
  };

  handleTransactionChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.value;
    if (name === "card_token") {
      const selectedCard = this.availableStorefrontCreditCards().find((cc) => cc.token === value);
    }
    let newTransaction = this.state.transaction;
    newTransaction[name] = value;
    this.setState({ transaction: newTransaction }, () => this.updateSurchargeInStore());
  };

  handlePaymentTypeChange = (event) => {
    const value = event.target.value;
    let newTransaction = {
      ...this.state.transaction,
      payment_type: value,
      bank_token: "",
      card_token: "",
    };
    this.setState({ paymentSelection: value, transaction: newTransaction }, () => this.updateSurchargeInStore());
  };

  handleAmountTypeChange = (event) => {
    const newAmountType = event.target.value;
    this.setState({
      amountType: newAmountType,
      customAmount: this.getPaymentAmount(newAmountType),
    }, () => this.updateSurchargeInStore());
  };

  getPaymentAmount = (amountType) => {
    const { rental } = this.props;
    const amountDue = rental.hasDeposit
      ? 0
      : Number(rental.minimumDeposit) - Number(rental.paymentBalance);
    switch (amountType) {
      case "amountDue":
        return this.calculateMinimumDeposit();
      case "amountRemaining":
        return this.calculateAmountDue();
      case "custom":
        return this.state.customAmount;
      default:
        return 0;
    }
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: toNumber(value) }, () => this.updateSurchargeInStore());
  };

  ensureMinimumDeposit = (e) => {
    const minimumDeposit = this.calculateMinimumDeposit();
    if (this.state.customAmount < minimumDeposit) {
      this.setState({ customAmount: minimumDeposit });
    }
  };

  handleEditAmount = () => {
    this.setState({
      transaction: {
        ...this.state.transaction,
        card_token: "",
        bank_token: "",
      },
    });
  };

  availableStorefrontCreditCards = () => {
    const { customer, location } = this.props;
    const locationPaymentProcessor = location.paymentProcessor;
    const processorMatchedCards = (customer.creditCards || []).filter((card) => {
      const processorMatches =
        (locationPaymentProcessor === "launchpay" && card.paymentProcessor === "worldpay") ||
        card.paymentProcessor === locationPaymentProcessor;

      return processorMatches;
    });

    const hasAddedViaData = processorMatchedCards.some((card) => card.addedVia != null);
    if (!hasAddedViaData) return processorMatchedCards;

    return processorMatchedCards.filter((card) => card.addedVia === "storefront");
  };

  renderNewCreditCardSection() {
    const { location, customer } = this.props;
    const { worldpayModalOpen, stripeModalOpen, cardNickname } = this.state;

    switch (location.paymentProcessor) {
      case "worldpay":
      case "launchpay":
        return (
          <div>
            <div style={{ marginBottom: "15px" }}>
              <label htmlFor="card-nickname" style={{ display: "block", marginBottom: "5px", fontWeight: "normal" }}>
                Nickname for Card (optional)
              </label>
              <input
                id="card-nickname"
                type="text"
                maxLength={10}
                placeholder="Nickname for Card (i.e., 'Work')"
                value={cardNickname}
                onChange={(e) => this.setState({ cardNickname: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
            <button
              type="button"
              className="btn full"
              onClick={this.toggleWorldpayModal}
              style={{ marginBottom: "15px" }}
            >
              Add Credit Card Securely
            </button>
            <WorldpayHostedPaymentModal
              open={worldpayModalOpen}
              toggle={this.toggleWorldpayModal}
              title="Add Credit Card"
              location={location}
              customerId={customer.id}
              customerType="Customer"
              nickname={cardNickname}
              onCardSaved={this.handleWorldpayCardSaved}
              onError={(error) => {
                this.props.setErrors({ body: error });
              }}
            />
          </div>
        );
      case "stripe":
        return (
          <div>
            <div style={{ marginBottom: "15px" }}>
              <label htmlFor="card-nickname-stripe" style={{ display: "block", marginBottom: "5px", fontWeight: "normal" }}>
                Nickname for Card (optional)
              </label>
              <input
                id="card-nickname-stripe"
                type="text"
                maxLength={10}
                placeholder="Nickname for Card (i.e., 'Work')"
                value={cardNickname}
                onChange={(e) => this.setState({ cardNickname: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
            <button
              type="button"
              className="btn full"
              onClick={this.toggleStripeModal}
              style={{ marginBottom: "15px" }}
            >
              Add Credit Card Securely
            </button>
            <StripeSecureCardModal
              open={stripeModalOpen}
              toggle={this.toggleStripeModal}
              title="Add Credit Card"
              location={location}
              nickname={cardNickname}
              onCardSaved={this.handleStripeCardSaved}
              onError={(error) => {
                this.props.setErrors({ body: error });
              }}
            />
            <p style={{ marginTop: "15px", marginBottom: "15px" }}>
              By clicking &quot;Add Credit Card Securely,&quot; you authorize us to securely store your encrypted payment card on
              file for future charges and orders.
            </p>
          </div>
        );
      default:
        return null;
    }
  }

  render() {
    const {
      errors,
      paymentSelection,
      customAmount,
      amountType,
      transaction,
    } = this.state;
    const { location, customer, mode, creditCardPercent, rental } = this.props;

    return (
      <div>
        <section>
          <ScrollArea speed={0.8} horizontal={false}>
            <div className="details">
              {mode === "customerLoggedIn" ? (
                <div className="fields amount">
                  {(() => {
                    const paymentMethodSelected =
                      (paymentSelection === "credit_card" &&
                        transaction.card_token &&
                        transaction.card_token !== "new_credit_card") ||
                      (paymentSelection === "bank" &&
                        transaction.bank_token &&
                        transaction.bank_token !== "new_bank");
                    const currentAmount = this.getPaymentAmount(amountType);
                    if (paymentMethodSelected) {
                      const amountDue = Number(currentAmount) || 0;
                      const hasSurcharge = paymentSelection === "credit_card" && location.chargeCreditCardFee && (rental?.rentalSetting == null || rental?.rentalSetting?.applyCcFee) && creditCardPercent > 0;
                      const isCardSelected = transaction.card_token && transaction.card_token !== "new_credit_card";
                      // Only apply surcharge for cards with binType === 'credit'
                      let isCreditBinType = true;
                      if (isCardSelected) {
                        const selectedCard = this.availableStorefrontCreditCards().find((cc) => cc.token === transaction.card_token);
                        if (selectedCard) {
                          isCreditBinType = selectedCard.binType === 'credit';
                        }
                      }
                      const surchargeAmount = hasSurcharge && isCreditBinType ? (creditCardPercent / 100) * amountDue : 0;
                      const showSurcharge = hasSurcharge && isCreditBinType;
                      const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' };
                      return (
                        <div style={{ width: '100%', gridColumn: '1 / -1' }}>
                          <div style={rowStyle}>
                            <span>Payment Subtotal</span>
                            <strong style={{ marginRight: 0 }}><CurrencyLabel value={amountDue} /></strong>
                          </div>
                          {showSurcharge && (
                            <>
                              <div style={rowStyle}>
                                <span>{isCardSelected ? 'CC Surcharge' : 'CC Surcharge Estimate'}</span>
                                <span>{!isCardSelected && '~'}<CurrencyLabel value={surchargeAmount} /></span>
                              </div>
                              <div style={{ ...rowStyle, borderTop: '2px solid', marginTop: '4px', paddingTop: '6px' }}>
                                <strong style={{ marginRight: 0 }}>Payment Total</strong>
                                <strong style={{ marginRight: 0 }}><CurrencyLabel value={amountDue + surchargeAmount} /></strong>
                              </div>
                            </>
                          )}
                          <div style={{ marginTop: '8px' }}>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                this.handleEditAmount();
                              }}
                              style={{ color: '#007cba', textDecoration: 'underline' }}
                            >
                              Edit Amount
                            </a>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <>
                        <label style={{ gridColumn: '1 / -1' }}>Pay With:</label>
                        <RadioGroup
                          name="amountType"
                          className="radioList"
                          value={amountType}
                          onChange={this.handleAmountTypeChange}
                        >
                          <FormControlLabel
                            label="Amount Due"
                            value="amountRemaining"
                            control={<Radio />}
                          />
                          <FormControlLabel
                            label="Minimum Deposit to Reserve"
                            value="amountDue"
                            control={<Radio />}
                          />
                          <FormControlLabel
                            label="Custom Amount"
                            value="custom"
                            control={<Radio />}
                          />
                        </RadioGroup>
                        <strong>
                          <CurrencyLabel
                            value={this.getPaymentAmount("amountRemaining")}
                          />
                        </strong>
                        <strong>
                          <CurrencyLabel
                            value={this.getPaymentAmount("amountDue")}
                          />
                        </strong>
                        <CurrencyInput
                          name="customAmount"
                          type="text"
                          placeholder="0.00"
                          className={classnames({
                            med: true,
                            error: errors.amount,
                          })}
                          value={customAmount}
                          onChange={this.handleChange}
                          onBlur={this.ensureMinimumDeposit}
                        />
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="guest-pay">
                  <label>Pay With:</label>
                  <p>
                    Guests placing a reservation are required to pay the full
                    amount due. To pay a partial amount,{" "}
                    <span
                      className="action-text"
                      onClick={() => this.props.toggleSignInOpen()}
                    >
                      Sign In
                    </span>{" "}
                    or{" "}
                    <span
                      className="action-text"
                      onClick={() => this.props.toggleSignUpOpen()}
                    >
                      Create a free account
                    </span>
                  </p>
                  <div className="amount-due">
                    <p>Amount due</p>
                    <strong>
                      <CurrencyLabel
                        value={this.getPaymentAmount("amountRemaining")}
                      />
                    </strong>
                  </div>
                </div>
              )}

              <div className="fields">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <RadioGroup
                    name="paymentType"
                    className="radioList"
                    onChange={this.handlePaymentTypeChange}
                    value={paymentSelection}
                    style={{ flex: 'none' }}
                  >
                    <FormControlLabel
                      value="credit_card"
                      control={<Radio />}
                      label="Credit Card"
                    />
                    {(location.paymentProcessor !== "propay" ||
                      (location.paymentProcessor === "propay" &&
                        location.propayCountry === "USA")) && (
                        <FormControlLabel
                          value="bank"
                          control={<Radio />}
                          label="Bank Account"
                        />
                      )}
                  </RadioGroup>
                  {location.chargeCreditCardFee && (rental?.rentalSetting == null || rental?.rentalSetting?.applyCcFee) && creditCardPercent > 0 && paymentSelection === "credit_card" && (() => {
                    const isCardSelected = transaction.card_token
                      && transaction.card_token !== "new_credit_card";
                    // Only apply surcharge for cards with binType === 'credit'
                    let isCreditBinType = true;
                    if (isCardSelected) {
                      const selectedCard = this.availableStorefrontCreditCards().find((cc) => cc.token === transaction.card_token);
                      if (selectedCard) {
                        isCreditBinType = selectedCard.binType === 'credit';
                      }
                    }
                    const surchargeAmount = isCreditBinType ? (creditCardPercent / 100) * this.getPaymentAmount(amountType) : 0;
                    if (surchargeAmount <= 0) return null;
                    return (
                      <span style={{ color: '#999', fontSize: '0.9em', whiteSpace: 'nowrap', paddingTop: '12px' }}>
                        (+<CurrencyLabel value={surchargeAmount} /> {isCardSelected ? 'Surcharge' : 'Estimated Surcharge'})
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="fields">
                {paymentSelection === "credit_card" && (
                  <StandardSelectField
                    meta={{}}
                    input={{
                      name: "card_token",
                      value: transaction.card_token,
                      onChange: this.handleTransactionChange,
                    }}
                  >
                    <option value="">Select a Credit Card</option>
                    <option value="new_credit_card">New Credit Card</option>
                    {this.availableStorefrontCreditCards().map((credit_card) => (
                        <option key={credit_card.id} value={credit_card.token}>
                          {credit_card.name}
                        </option>
                      ))}
                  </StandardSelectField>
                )}
                {transaction.card_token === "new_credit_card" && this.renderNewCreditCardSection()}
                {paymentSelection === "bank" && (
                  <StandardSelectField
                    meta={{}}
                    input={{
                      name: "bank_token",
                      value: transaction.bank_token,
                      onChange: this.handleTransactionChange,
                    }}
                  >
                    <option value="">Select a Bank Account</option>
                    <option value="new_bank">New Bank Account</option>
                    {customer &&
                      customer.bankAccounts &&
                      customer.bankAccounts
                        .filter(
                          (ba) =>
                            ba.paymentProcessor === location.paymentProcessor
                        )
                        .map((bankAccount) => (
                          <option
                            key={bankAccount.id}
                            value={bankAccount.token}
                          >{`${bankAccount.bankInfo.bankName} ${bankAccount.bankInfo.last4}`}</option>
                        ))}
                  </StandardSelectField>
                )}
                {paymentSelection === "bank" &&
                  transaction.bank_token === "new_bank" &&
                  location.paymentProcessor === "stripe" && (
                    <>
                      {this.state.new_bank && this.state.new_bank.payment_method_id ? (
                        <div className="bank-connected-text">
                          <p>
                            Bank account connected. Click Pay &amp; Reserve Now
                            to proceed with payment.
                          </p>
                          <a onClick={this.handleChangeStripeBank}>Use a different bank</a>
                        </div>
                      ) : (
                        <a className="btn full" onClick={this.handleStripeBankClick}>
                          Pay with Bank Account
                        </a>
                      )}
                      <form
                        id="payment-form"
                        onSubmit={this.handleSubmit}
                        className="form"
                      ></form>
                    </>
                  )}
                {paymentSelection === "bank" &&
                  transaction.bank_token === "new_bank" &&
                  location.paymentProcessor === "propay" && (
                    <PropayBankForm
                      onSubmit={this.handleSubmitNewPropayBank}
                      type="no-submit-button"
                    />
                  )}
                {paymentSelection === "bank" &&
                  transaction.bank_token === "new_bank" &&
                  (location.paymentProcessor === "worldpay" ||
                    location.paymentProcessor === "launchpay") &&
                  location.hasGrailpayEnabled && (
                    <WorldpayBankForm
                      onSubmit={this.handleSubmitNewPropayBank}
                      type="no-submit-button"
                    />
                  )}
                {((paymentSelection === "credit_card" &&
                  transaction.card_token !== "new_credit_card") ||
                  (paymentSelection === "bank" &&
                    transaction.bank_token !== "new_bank")) && (
                    <form
                      id="payment-form"
                      onSubmit={this.handleSubmit}
                      className="form"
                    ></form>
                  )}
              </div>
              {paymentSelection === "credit_card" && (
                <p style={{ marginTop: '15px', marginBottom: '15px' }}>
                  By clicking “Make Payment,” you authorize us to securely
                  store your encrypted payment card on file for future charges and orders.
                </p>
              )}
            </div>
          </ScrollArea>
        </section>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const location = ownProps.location ? ownProps.location : state.locations.location;
  const customer = ownProps.customer ? ownProps.customer : state.customer.customer;
  const cart = state.cart;
  const mode = ownProps.mode;
  const creditCardPercent = location.creditCardPercent;
  return { creditCardPercent, location, customer, cart, mode };
};

export default connect(mapStateToProps, actions)(AutobookPayment);
